import React, { useEffect, useState } from 'react';
import type { LicensePlan } from 'edinshall-licensing-shared';
import { usePortalContext } from '../context/PortalContext';

/**
 * "Your support plan" (tracker #106 D17/D18, owner rulings 2026-09-13).
 *
 * Tells the tenant what assisted support their plan includes, the first-
 * response target, and — where one exists — the step up. Copy states what is
 * INCLUDED, never what is charged: support billing emission is a separate
 * owner-controlled switch. Targets are calendar hours (D18: "48 hours is two
 * rotations around the sun"); inclusions come from the consumption API so
 * they track the canonical meter matrix rather than a copy here.
 */

interface ConsumptionMeter {
  meterId: string;
  monthlyIncludedRaw: number;
}

interface ConsumptionView {
  plan: LicensePlan;
  onboarding: { supportAllowanceHours: number; windowEndsAt: string | null };
  meters: ConsumptionMeter[];
}

/** First-response targets per plan, calendar hours (support-desk-design.md §5 decision 1, D18). */
const FIRST_RESPONSE_HOURS: Record<LicensePlan, number | null> = { free: null, standard: 48, enterprise: 8 };
const PLAN_LABEL: Record<LicensePlan, string> = { free: 'Free', standard: 'Standard', enterprise: 'Enterprise' };
const MARKETPLACE_URL = 'https://appsource.microsoft.com/';

const hoursWord = (hours: number): string => `${hours} hour${hours === 1 ? '' : 's'}`;

export interface SupportPlanFacts {
  plan: LicensePlan;
  includedHoursPerMonth: number;
  onboardingAllowanceHours: number;
  onboardingWindowEndsAt: string | null;
}

/** Pure — exported for tests. */
export const describeSupportPlan = (facts: SupportPlanFacts, now: Date = new Date()): { headline: string; detail: string[]; upgrade?: { text: string; href: string } } => {
  const target = FIRST_RESPONSE_HOURS[facts.plan];
  const detail: string[] = [];
  if (facts.plan === 'free') {
    return {
      headline: 'Your plan: Free — self-help and best-effort escalation.',
      detail: [
        'Escalations are answered as capacity allows; there is no response-time commitment on the Free plan.',
        'Standard includes 2 hours of assisted support a month with a first response within 48 hours, plus a 20-hour onboarding allowance in the first 90 days.'
      ],
      upgrade: { text: 'Compare plans on Microsoft AppSource', href: MARKETPLACE_URL }
    };
  }
  const headline = `Your plan: ${PLAN_LABEL[facts.plan]} — ${hoursWord(facts.includedHoursPerMonth)} of assisted support a month included, first response within ${target} hours.`;
  const windowEnd = facts.onboardingWindowEndsAt ? new Date(facts.onboardingWindowEndsAt) : null;
  if (facts.onboardingAllowanceHours > 0 && windowEnd && !Number.isNaN(windowEnd.getTime()) && windowEnd > now) {
    detail.push(`Your ${hoursWord(facts.onboardingAllowanceHours)} onboarding allowance also applies until ${windowEnd.toLocaleDateString()}.`);
  }
  detail.push('Assisted support beyond the inclusion is available at the published hourly rate.');
  if (facts.plan === 'standard') {
    detail.push('Enterprise includes 8 hours a month with a first response within 8 hours.');
    return { headline, detail, upgrade: { text: 'Compare plans on Microsoft AppSource', href: MARKETPLACE_URL } };
  }
  return { headline, detail };
};

const SupportPlanNotice = (): JSX.Element | null => {
  const { tenant } = usePortalContext();
  const [facts, setFacts] = useState<SupportPlanFacts>();

  useEffect(() => {
    let disposed = false;
    const fallbackPlan = tenant?.planCode;
    fetch('/api/account/consumption')
      .then(async (response) => (response.ok ? ((await response.json()) as ConsumptionView) : null))
      .then((view) => {
        if (disposed) return;
        if (view) {
          const supportMeter = view.meters.find((meter) => meter.meterId === 'support.hours');
          setFacts({
            plan: view.plan,
            includedHoursPerMonth: supportMeter?.monthlyIncludedRaw ?? 0,
            onboardingAllowanceHours: view.onboarding?.supportAllowanceHours ?? 0,
            onboardingWindowEndsAt: view.onboarding?.windowEndsAt ?? null
          });
        } else if (fallbackPlan) {
          setFacts({
            plan: fallbackPlan,
            includedHoursPerMonth: fallbackPlan === 'enterprise' ? 8 : fallbackPlan === 'standard' ? 2 : 0,
            onboardingAllowanceHours: 0,
            onboardingWindowEndsAt: null
          });
        }
      })
      .catch(() => {
        if (!disposed && fallbackPlan) {
          setFacts({
            plan: fallbackPlan,
            includedHoursPerMonth: fallbackPlan === 'enterprise' ? 8 : fallbackPlan === 'standard' ? 2 : 0,
            onboardingAllowanceHours: 0,
            onboardingWindowEndsAt: null
          });
        }
      });
    return () => {
      disposed = true;
    };
  }, [tenant?.planCode]);

  if (!facts) {
    return null;
  }
  const described = describeSupportPlan(facts);
  return (
    <div className="card" style={{ marginTop: '12px' }} data-testid="support-plan-notice">
      <strong>{described.headline}</strong>
      {described.detail.map((line) => (
        <p key={line} className="muted" style={{ margin: '6px 0 0' }}>{line}</p>
      ))}
      {described.upgrade && (
        <p style={{ margin: '8px 0 0' }}>
          <a href={described.upgrade.href} target="_blank" rel="noreferrer">{described.upgrade.text}</a>
          {' · '}
          <a href="/account">See your usage on the Account page</a>
        </p>
      )}
    </div>
  );
};

export default SupportPlanNotice;
